import React, {useState} from 'react'
import _ from 'lodash'
import DeleteDesignVariantImage from "./delete-design-variant-image";
import EditFontDetails from "../settings/edit-font-details";
import {
    DESIGN_KEY_PREFIX,
    FLIP_DIRECTION_KEY, REPEAT_IMAGE_FIELD_LIST, RECTANGLE_FIELD_LIST, TEMPLATE_IMAGE_FIELD_LIST
} from "../../../constants/design.constant";
import EditColorDetails from "../settings/edit-color-details";
import EditStrokeDetails from "../settings/edit-stroke-details";
import EditShadowDetails from "../settings/edit-shadow-details";
import {processColorDetails} from "../../../../common/utils/color-util";
import EditFlipDetails from "../settings/edit-flip-details";
import EditRotationDetails from "../settings/edit-rotation-details";
import {getNextItemKey} from "../../../../common/utils/item-key-util";
import {DocumentDuplicateIcon} from "@heroicons/react/24/solid";
import EditTextPathDetails from "../settings/edit-text-path-details";
import {processTextPathData} from "../../../utils/svg-data.util";
import DeleteShape from "../shapes/delete-shape";
import RenderFields from "../../../../common/ui/render-fields";
import SelectColorTypeDetails from "../settings/select-color-type-details";

const DesignVariantLayers = ({designVariantDetail, setDesignVariantDetail}) => {
    const [selectedTextItem, setSelectedTextItem] = useState(null);
    const [selectedImageItem, setSelectedImageItem] = useState(null);
    const [selectedShape, setSelectedShape] = useState(null);

    const handleRemoveImage = (image, imageIndex) => {
        const existingDesignVariantDetail = {...designVariantDetail};

        if (existingDesignVariantDetail.images.length > 1) {
            delete existingDesignVariantDetail.images[imageIndex];
            existingDesignVariantDetail.images = _.compact(existingDesignVariantDetail.images);
        } else {
            delete existingDesignVariantDetail.images;
        }

        setDesignVariantDetail(existingDesignVariantDetail);
    }

    const handleEditTextDetail = (textItem, index) => {
        setSelectedTextItem(textItem);
    }

    const handleEditImageDetail = (imageItem, index) => {
        setSelectedImageItem(imageItem);
    }

    const handleDuplicateImageDetail = (imageItem) => {
        const existingDesignVariantDetail = {...designVariantDetail};
        const newImage = _.cloneDeep(imageItem);
        newImage.key = getNextItemKey(existingDesignVariantDetail.images, DESIGN_KEY_PREFIX.IMAGE);
        newImage.x = 200;
        newImage.y = 200;
        existingDesignVariantDetail.images.push(newImage);
        setDesignVariantDetail(existingDesignVariantDetail);
    }

    const handleTextDetailChanges = (event, textItem) => {
        const {name, value, type} = event.target;
        const existingDesignVariantDetail = {...designVariantDetail};
        const itemIndex = _.findIndex(existingDesignVariantDetail.textItems, ['key', textItem.key]);

        if (type === 'number') {
            existingDesignVariantDetail.textItems[itemIndex][name] = _.parseInt(value);
        } else {
            existingDesignVariantDetail.textItems[itemIndex][name] = value;
        }

        if (name === 'colorKey' || name === 'colorCodeKey' ||
            name === 'strokeColorKey' || name === 'strokeColorCodeKey' ||
            name === 'shadowColorKey' || name === 'shadowColorCodeKey') {
            processColorDetails(existingDesignVariantDetail.textItems[itemIndex]);
        } else if (name === 'radius' || name === 'rotation' || name === 'letterSpacing' ||
            name === 'startAngle' || name === 'sweepAngle') {
            processTextPathData(existingDesignVariantDetail.textItems[itemIndex]);
        }

        setDesignVariantDetail(existingDesignVariantDetail);
    }

    const handleImageDetailChanges = (event, imageItem) => {
        const {name, value, type} = event.target;
        const existingDesignVariantDetail = {...designVariantDetail};
        const itemIndex = _.findIndex(existingDesignVariantDetail.images, ['key', imageItem.key]);

        if (type === 'number') {
            existingDesignVariantDetail.images[itemIndex][name] = _.parseInt(value);
        } else if (type === 'checkbox') {
            existingDesignVariantDetail.images[itemIndex][name] = !existingDesignVariantDetail.images[itemIndex][name];
        } else {
            existingDesignVariantDetail.images[itemIndex][name] = value;
        }

        if (name === 'flip') {
            if (value === FLIP_DIRECTION_KEY.HORIZONTAL) {
                imageItem.scaleX = -imageItem.scaleX;
            } else if (value === FLIP_DIRECTION_KEY.VERTICAL) {
                imageItem.scaleY = -imageItem.scaleY;
            }

            existingDesignVariantDetail.images[itemIndex] = imageItem;
        } else if (name === 'size') {
            let size = _.parseInt(value);
            if (_.isNaN(size)) {
                size = 100;
            }

            imageItem.width = imageItem.imageWidth * size / 100;
            imageItem.height = imageItem.imageHeight * size / 100;
            existingDesignVariantDetail.images[itemIndex] = imageItem;
        } else if (name === 'fillPatternOffsetXPercentage') {
            let fillPatternOffsetXPercentage = _.parseInt(value);
            if (_.isNaN(fillPatternOffsetXPercentage)) {
                fillPatternOffsetXPercentage = 0;
            }

            imageItem.fillPatternOffsetX = -imageItem.width * fillPatternOffsetXPercentage / 100;
        } else if (name === 'fillPatternOffsetYPercentage') {
            let fillPatternOffsetYPercentage = _.parseInt(value);
            if (_.isNaN(fillPatternOffsetYPercentage)) {
                fillPatternOffsetYPercentage = 0;
            }

            imageItem.fillPatternOffsetY = -imageItem.width * fillPatternOffsetYPercentage / 100;
        }

        if (imageItem?.isBackgroundImage) {
            imageItem.fillPatternScaleX = imageItem?.height / imageItem?.imageHeight;
            imageItem.fillPatternScaleY = imageItem?.height / imageItem?.imageHeight;
        }

        setDesignVariantDetail(existingDesignVariantDetail);
    }

    const handleRemoveShape = (shapeDetail, shapeDetailIndex) => {
        const existingDesignVariantDetail = {...designVariantDetail};

        if (existingDesignVariantDetail.shapes.length > 1) {
            delete existingDesignVariantDetail.shapes[shapeDetailIndex];
            existingDesignVariantDetail.shapes = _.compact(existingDesignVariantDetail.shapes);
        } else {
            delete existingDesignVariantDetail.shapes;
        }

        setDesignVariantDetail(existingDesignVariantDetail);
    }

    const handleSelectShape = (shapeDetail, index) => {
        setSelectedShape(shapeDetail);
    }

    const handleShapeDetailChanges = (event, shapeDetail) => {
        const {name, value, type} = event.target;
        const existingDesignVariantDetail = {...designVariantDetail};
        const itemIndex = _.findIndex(existingDesignVariantDetail.shapes, ['key', shapeDetail.key]);

        if (type === 'number') {
            existingDesignVariantDetail.shapes[itemIndex][name] = _.parseInt(value);
        } else {
            existingDesignVariantDetail.shapes[itemIndex][name] = value;
        }

        if (name === 'colorKey' || name === 'colorCodeKey' ||
            name === 'strokeColorKey' || name === 'strokeColorCodeKey' ||
            name === 'shadowColorKey' || name === 'shadowColorCodeKey') {
            processColorDetails(existingDesignVariantDetail.shapes[itemIndex]);
        } else if (name === 'radius' || name === 'rotation' || name === 'letterSpacing' ||
            name === 'startAngle' || name === 'sweepAngle') {
            processTextPathData(existingDesignVariantDetail.shapes[itemIndex]);
        }

        setDesignVariantDetail(existingDesignVariantDetail);
    }

    const handleClearShapeColor = (event, shapeDetail) => {
        const existingDesignVariantDetail = {...designVariantDetail};
        const itemIndex = _.findIndex(existingDesignVariantDetail.shapes, ['key', shapeDetail.key]);

        existingDesignVariantDetail.shapes[itemIndex]['colorKey'] = '';
        existingDesignVariantDetail.shapes[itemIndex]['colorCodeKey'] = '';
        existingDesignVariantDetail.shapes[itemIndex]['color'] = '';
        existingDesignVariantDetail.shapes[itemIndex]['fill'] = '';
        setDesignVariantDetail(existingDesignVariantDetail);
    }

    const handleClearColorInTextDetail = (attributeList, textItem) => {
        const existingDesignVariantDetail = {...designVariantDetail};
        const itemIndex = _.findIndex(existingDesignVariantDetail.textItems, ['key', textItem.key]);

        _.forEach(attributeList, attribute => {
            existingDesignVariantDetail.textItems[itemIndex][attribute] = '';
        });

        if (attributeList.indexOf('color') !== -1) {
            existingDesignVariantDetail.textItems[itemIndex]['fill'] = '';
        }

        setDesignVariantDetail(existingDesignVariantDetail);
    }

    return (
        <>
            <table className="w-full">
                <thead className="">
                <tr>
                    <th className="p-2 border border-gray-400 bg-gray-400 text-white">
                        Texts
                    </th>
                </tr>
                </thead>
                <tbody className="">
                {designVariantDetail?.textItems?.map((textItem, index) => (
                    <tr key={`text-${index}`} className="">
                        <td className={`p-2 border border-gray-400 bg-gray-100 ${textItem.key === selectedTextItem?.key ? 'bg-blue-100' : ''}`}>
                            <div className="flex justify-between items-center">
                                <div
                                    className={`align-middle pl-2 ${textItem.key === selectedTextItem?.key ? 'font-bold' : ''}`}>
                                    {textItem.text}
                                </div>
                                <div className="text-right">
                                    {textItem.key === selectedTextItem?.key &&
                                        <button
                                            className="button-small-link"
                                            onClick={() => setSelectedTextItem(null)}>
                                            Close
                                        </button>
                                    }

                                    {textItem.key !== selectedTextItem?.key &&
                                        <button
                                            className="button-small-link"
                                            onClick={() => handleEditTextDetail(textItem, index)}>
                                            Edit
                                        </button>
                                    }
                                </div>
                            </div>

                            {textItem.key === selectedTextItem?.key &&
                                <>
                                    <div className="mt-2">
                                        <EditFontDetails
                                            displayTextItem={textItem}
                                            handleFontDetailsChanges={handleTextDetailChanges}/>

                                        <SelectColorTypeDetails
                                            label={'Text Color & Shade'}
                                            parentForm={textItem}
                                            colorAttribute={'color'}
                                            colorKeyAttribute={'colorKey'}
                                            colorCodeKeyAttribute={'colorCodeKey'}
                                            settings={designVariantDetail?.settings}
                                            handleColorDetailsChanges={handleTextDetailChanges}
                                            handleClearColorDetailsChanges={handleClearColorInTextDetail}/>

                                        {/*<EditColorDetails
                                            displayTextItem={textItem}
                                            handleColorDetailsChanges={handleTextDetailChanges}
                                            handleClearColor={undefined}/>*/}

                                        <EditStrokeDetails
                                            parentForm={textItem}
                                            handleStrokeDetailsChanges={handleTextDetailChanges}/>

                                        <EditShadowDetails
                                            parentForm={textItem}
                                            handleShadowDetailsChanges={handleTextDetailChanges}/>

                                        <EditRotationDetails
                                            parentItem={textItem}
                                            handleRotationDetailsChanges={handleTextDetailChanges}/>

                                        <EditTextPathDetails
                                            parentItem={textItem}
                                            handleTextPathDetailsChanges={handleTextDetailChanges}/>
                                    </div>
                                </>
                            }
                        </td>
                    </tr>
                ))}
                </tbody>
            </table>

            <table className="w-full mt-4">
                <thead className="">
                <tr>
                    <th className="p-2 border border-gray-400 bg-gray-400 text-white">
                        Shapes
                    </th>
                </tr>
                </thead>
                <tbody className="">
                {designVariantDetail?.shapes?.map((shapeDetail, index) => (
                    <tr key={`added-shape-${index}`} className="">
                        <td className={`p-2 border border-gray-400 bg-gray-100 ${shapeDetail.key === selectedShape?.key ? 'bg-blue-100' : ''}`}>
                            <div className="flex justify-between items-center">
                                <div
                                    className={`align-middle pl-2 ${shapeDetail.key === selectedShape?.key ? 'font-bold' : ''}`}>
                                    {shapeDetail.key}
                                </div>
                                <div className="text-right">
                                    {shapeDetail.key === selectedShape?.key &&
                                        <button
                                            className="button-small-link mr-2"
                                            onClick={() => setSelectedShape(null)}>
                                            Close
                                        </button>
                                    }

                                    {shapeDetail.key !== selectedShape?.key &&
                                        <button
                                            className="button-small-link mr-2"
                                            onClick={() => handleSelectShape(shapeDetail, index)}>
                                            Edit
                                        </button>
                                    }

                                    <DeleteShape
                                        shapeDetail={shapeDetail}
                                        removeShape={handleRemoveShape}
                                        shapeDetailIndex={index}/>
                                </div>
                            </div>

                            {shapeDetail.key === selectedShape?.key &&
                                <>
                                    <div className="mt-2">

                                        <div className="flex flex-wrap justify-start">
                                            <RenderFields
                                                parentForm={shapeDetail}
                                                handleDetailChanges={(event) => {
                                                    handleShapeDetailChanges(event, shapeDetail);
                                                }}
                                                fieldList={_.groupBy(RECTANGLE_FIELD_LIST, 'group')['dimension']}
                                                enableEdit={true}
                                                fieldClassName={'w-1/2'}/>
                                        </div>

                                        <EditColorDetails
                                            displayTextItem={shapeDetail}
                                            handleColorDetailsChanges={handleShapeDetailChanges}
                                            handleClearColor={handleClearShapeColor}/>

                                        <EditStrokeDetails
                                            parentForm={shapeDetail}
                                            handleStrokeDetailsChanges={handleShapeDetailChanges}/>

                                        <EditShadowDetails
                                            parentForm={shapeDetail}
                                            handleShadowDetailsChanges={handleShapeDetailChanges}/>

                                        <EditRotationDetails
                                            parentItem={shapeDetail}
                                            handleRotationDetailsChanges={handleShapeDetailChanges}/>

                                        <div className="flex flex-wrap justify-start">
                                            <RenderFields
                                                parentForm={shapeDetail}
                                                handleDetailChanges={(event) => {
                                                    handleShapeDetailChanges(event, shapeDetail);
                                                }}
                                                fieldList={_.groupBy(RECTANGLE_FIELD_LIST, 'group')['line']}
                                                enableEdit={true}
                                                fieldClassName={'w-1/2'}/>
                                        </div>

                                    </div>
                                </>
                            }
                        </td>
                    </tr>
                ))}

                {!(designVariantDetail?.shapes?.length > 0) &&
                    <tr className="">
                        <td className="p-2 border border-gray-400 bg-gray-100 m-auto flex justify-between items-center">
                            <div className="align-middle m-auto p-2 text-red-500">
                                No Shape Added
                            </div>
                        </td>
                    </tr>
                }

                </tbody>
            </table>

            <table className="w-full mt-4">
                <thead className="">
                <tr>
                    <th className="p-2 border border-gray-400 bg-gray-400 text-white">
                        Images
                    </th>
                </tr>
                </thead>
                <tbody className="">
                {designVariantDetail?.images?.map((imageDetail, index) => (
                    <tr key={`image-${index}`} className="">
                        <td className={`p-2 border border-gray-400 bg-gray-100 ${imageDetail.key === selectedImageItem?.key ? 'bg-blue-100' : ''}`}>
                            <div className="flex justify-between items-center">
                                <div
                                    className={`align-middle pl-2 ${imageDetail.key === selectedImageItem?.key ? 'font-bold' : ''}`}>
                                    {imageDetail.displayLabel}
                                </div>
                                <div className="text-right items-center flex">
                                    {imageDetail.key === selectedImageItem?.key &&
                                        <button
                                            className="button-small-link mr-2"
                                            onClick={() => setSelectedImageItem(null)}>
                                            Close
                                        </button>
                                    }

                                    {imageDetail.key !== selectedImageItem?.key &&
                                        <button
                                            className="button-small-link mr-2"
                                            onClick={() => handleEditImageDetail(imageDetail, index)}>
                                            Edit
                                        </button>
                                    }

                                    <button onClick={() => handleDuplicateImageDetail(imageDetail)}
                                            className="button-outline-small px-2 mr-2">
                                        <div className="mr-1">
                                            <DocumentDuplicateIcon className="h-6 w-6"/>
                                        </div>
                                    </button>

                                    <DeleteDesignVariantImage
                                        image={imageDetail}
                                        removeImage={handleRemoveImage}
                                        imageIndex={index}/>
                                </div>
                            </div>

                            {imageDetail.key === selectedImageItem?.key &&
                                <>
                                    <div className="mt-2">
                                        <div className="field-container mt-2 mb-2 justify-start flex">
                                            <div className="justify-start flex">
                                                <input
                                                    type="checkbox"
                                                    className="h-6 w-6 mr-1"
                                                    id="isRepeatImage"
                                                    name="isRepeatImage"
                                                    value={imageDetail?.isRepeatImage}
                                                    checked={imageDetail?.isRepeatImage}
                                                    onChange={(event) => {
                                                        handleImageDetailChanges(event, imageDetail);
                                                    }}>
                                                </input>
                                                <div className=''>
                                                    <label className='field-label self-center'
                                                           htmlFor='isRepeatImage'>Repeat Image</label>
                                                </div>
                                            </div>

                                            <div className="justify-start flex">
                                                <input
                                                    type="checkbox"
                                                    className="h-6 w-6 mr-1"
                                                    id="isBackgroundImage"
                                                    name="isBackgroundImage"
                                                    value={imageDetail?.isBackgroundImage}
                                                    checked={imageDetail?.isBackgroundImage}
                                                    onChange={(event) => {
                                                        handleImageDetailChanges(event, imageDetail);
                                                    }}>
                                                </input>
                                                <div className=''>
                                                    <label className='field-label self-center'
                                                           htmlFor='isRepeatImage'>Background Image</label>
                                                </div>
                                            </div>

                                            <div className="justify-start flex ml-4">
                                                <input
                                                    type="checkbox"
                                                    className="h-6 w-6 mr-1"
                                                    id="isTemplate"
                                                    name="isTemplate"
                                                    value={imageDetail?.isTemplate}
                                                    checked={imageDetail?.isTemplate}
                                                    onChange={(event) => {
                                                        handleImageDetailChanges(event, imageDetail);
                                                    }}>
                                                </input>
                                                <div className=''>
                                                    <label className='field-label self-center'
                                                           htmlFor='isTemplate'>Template Image</label>
                                                </div>
                                            </div>
                                        </div>

                                        {!imageDetail?.isRepeatImage && !imageDetail?.isTemplate && !imageDetail?.isBackgroundImage &&
                                            <>
                                                <EditFlipDetails
                                                    item={imageDetail}
                                                    handleFlipDetailsChanges={handleImageDetailChanges}/>

                                                <EditRotationDetails
                                                    parentItem={imageDetail}
                                                    handleRotationDetailsChanges={handleImageDetailChanges}/>
                                            </>
                                        }

                                        {(imageDetail?.isBackgroundImage || imageDetail?.isRepeatImage) && !imageDetail?.isTemplate &&
                                            <div className="flex flex-wrap justify-start">
                                                <RenderFields
                                                    parentForm={imageDetail}
                                                    handleDetailChanges={(event) => {
                                                        handleImageDetailChanges(event, imageDetail);
                                                    }}
                                                    fieldList={_.groupBy(REPEAT_IMAGE_FIELD_LIST, 'group')['dimension']}
                                                    enableEdit={true}
                                                    fieldClassName={'w-1/2'}/>
                                            </div>
                                        }

                                        {imageDetail?.isTemplate &&
                                            <div className="flex flex-wrap justify-start">
                                                <RenderFields
                                                    parentForm={imageDetail}
                                                    handleDetailChanges={(event) => {
                                                        handleImageDetailChanges(event, imageDetail);
                                                    }}
                                                    fieldList={_.groupBy(TEMPLATE_IMAGE_FIELD_LIST, 'group')['template']}
                                                    enableEdit={true}
                                                    fieldClassName={'w-1/2'}/>
                                            </div>
                                        }

                                    </div>
                                </>
                            }

                        </td>
                    </tr>
                ))}

                {!(designVariantDetail?.images?.length > 0) &&
                    <tr className="">
                        <td className="p-2 border border-gray-400 bg-gray-100 m-auto flex justify-between items-center">
                            <div className="align-middle m-auto p-2 text-red-500">
                                No Image Added
                            </div>
                        </td>
                    </tr>
                }

                </tbody>
            </table>

        </>
    )
}

export default DesignVariantLayers
